/**
 * Simple Permission System for Menu Items
 * One function to check all permissions
 */

/**
 * Check if user can access a menu feature
 * @param {string} role - User role (ADMIN, CUSTOMER)
 * @param {string} type - User type (PRODUCCION, PROYECTOS, SAT1, SAT2, PROVEEDOR, CONTABILIDAD, MASTER for ADMIN; GROUP, LICENCIA_BASICA, LICENCIA_PRO, PDA, CASH for CUSTOMER)
 * @param {string} category - User category (not used in this matrix, but kept for compatibility)
 * @param {string} feature - Feature to check
 * @returns {boolean}
 */
export function canAccess(role, type, category, feature) {
  const r = role;
  const t = type;

  // Quick helpers

  //MAIN ROLES
  const isAdmin = r === 'ADMIN';
  const isCustomer = r === 'CUSTOMER';

  //CUSTOMER TYPES
  const isGroup = t === 'GROUP';
  const isLicenciaBasica = t === 'LICENCIA_BASICA';
  const isLicenciaPro = t === 'LICENCIA_PRO';
  const isPDA = t === 'PDA';
  const isCash = t === 'CASH';

  //ADMIN TYPES
  const isProduccion = t === 'PRODUCCION';
  const isProyectos = t === 'PROYECTOS';
  const isSat1 = t === 'SAT1';
  const isSat2 = t === 'SAT2';
  const isProveedor = t === 'PROVEEDOR';
  const isContabilidad = t === 'CONTABILIDAD';
  const isProgrammer = t === 'PROGRAMMER'; //EXTRA TYPE FOR PROGRAMMERS
  const isMaster = t === 'MASTER';

  // MASTER has access to everything
  if (isMaster) return true;

  // CONTABILIDAD has restricted access - only specific reporte features
  if (isContabilidad) {
    return ['sales', 'internalSales', 'internalSales', 'arqueos', 'kpi', 'customers'].includes(feature);
  }

  // PRODUCCION has restricted access - only Kioscos and Módulos
  if (isProduccion) {
    return ['machines', 'modules'].includes(feature);
  }

  // PROVEEDOR has restricted access - Módulos
  if (isProveedor) {
    return ['modules'].includes(feature);
  }

  // PROVEEDOR has restricted access - Módulos
  if (isProgrammer) {
    return ['machines'].includes(feature);
  }

  // Permission rules by menu feature based on the provided matrix
  switch (feature) {
    case 'nightscan':
      return (isAdmin && (isProyectos || isSat1 || isSat2));
    // Canales
    case 'machines':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro || isPDA || isCash));
    case 'qrs':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && isGroup);
    case 'pdas':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && isGroup);

    // Gestión
    case 'lockersale':
      return isCustomer && (isLicenciaBasica || isLicenciaPro);
    case 'pin':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro));
    case 'discount-codes':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro));
    case 'redeemcodes':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro));
    case 'rentals':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro || isPDA));
    case 'calendars':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro));
    case 'email-templates':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro));
    case 'partners':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) && (isProduccion || isProyectos || isSat1 || isSat2);
    case 'discountCodes':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro));

    // KPI
    case 'sales':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro));
    case 'internalsales':
      return (isAdmin && isProyectos) || (isCustomer && isGroup);
    case 'arqueos':
      return (isAdmin && isProyectos) || (isCustomer && isGroup);
    case 'kpi':
      return (isAdmin && isProyectos) || (isCustomer && isGroup);
    case 'ocupation':
      return (isAdmin && isProyectos) || (isCustomer && isGroup);
    case 'objectives':
      return (isAdmin && isProyectos) || (isCustomer && isGroup);
    case 'ocupationaccumulated':
      return (isAdmin && isProyectos) || (isCustomer && isGroup);
    case 'ocupationdaily':
      return (isAdmin && isProyectos) || (isCustomer && isGroup);
    case 'pinrecovers':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && isGroup);

    // Operativa
    case 'customers':
      return (isAdmin && (isProyectos || isSat1 || isSat2));
    case 'contracts':
      return (isAdmin && (isProyectos || isSat1 || isSat2));
    case 'withdrawals':
      return (isAdmin && isProyectos) || (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro));
    case 'audit':
      return (isAdmin && (isProyectos || isSat1 || isSat2));
    case 'tpvs':
      return (isAdmin && (isProyectos || isSat2));
    case 'modules':
      return (isAdmin && (isProyectos || isSat1 || isSat2));
    case 'matrixs':
      return (isAdmin && isProyectos);
    case 'lowbluetooth':
      return (isAdmin && isProyectos);
    case 'lockersqty':
      return (isAdmin && (isProyectos || isSat2));
    case 'openings':
      return (isAdmin && (isProyectos || isSat2));

    // SAT
    case 'supporttickets':
      return (isAdmin && (isSat1 || isSat2)) || (isCustomer && (isLicenciaBasica || isLicenciaPro));
    case 'incidences':
      return isAdmin && isSat2;
    case 'faults':
      return (isAdmin && (isSat1 || isSat2));
    case 'replacements':
      return isAdmin && isSat2;
    case 'bowler':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && isGroup);

    // Baqueira (Special access)
    case 'operators':
      return (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro || isPDA || isCash)); // Assuming ESPECIAL means access
    case 'rules':
      return (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro || isPDA || isCash));
    case 'articulos':
      return (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro || isPDA || isCash));
    case 'articles':
      return (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro || isPDA || isCash));
    case 'montarto':
      return (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro || isPDA || isCash));

    // Soporte
    case 'supportticketadd':
      return isCustomer;
    case 'support-chat':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isLicenciaBasica || isLicenciaPro || isPDA || isCash));
    case 'faqs':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isGroup || isLicenciaPro || isPDA || isCash));
    case 'manuals':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || isCustomer;

    // Admin
    case 'gameranking':
      return (isAdmin && isProyectos);

    default:
      return false;
  }
}

/**
 * Check if user has access to ANY feature in a category
 * @param {string} role - User role
 * @param {string} type - User type
 * @param {string} category - User category
 * @param {string[]} features - Array of features to check
 * @returns {boolean}
 */
export function hasAccessToAny(role, type, category, features) {
  return features.some(feature => canAccess(role, type, category, feature));
}

export default canAccess;
